/**
 * Rotten Charm — Product Gallery
 * Thumbnail rail swaps the active main image; a lightbox opens the full
 * media set for closer viewing. Also exposes window.RottenGallery.showMedia
 * so component-product-form.js can swap the active image when a variant
 * with its own featured image is selected.
 */
(function () {
  'use strict';

  function trapFocus(container, closeFn) {
    const focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return () => {};
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    function onKeydown(e) {
      if (e.key === 'Escape') {
        closeFn();
        return;
      }
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    first.focus();
    return () => container.removeEventListener('keydown', onKeydown);
  }

  class RottenGallery {
    constructor(el) {
      this.el = el;
      this.mainItems = Array.from(el.querySelectorAll('[data-rc-gallery-main-item]'));
      this.thumbs = Array.from(el.querySelectorAll('[data-rc-gallery-thumb]'));
      this.lightbox = document.querySelector('[data-rc-lightbox]');
      this.releaseFocusTrap = null;
      this.lightboxIndex = 0;

      this.bindThumbs();
      this.bindZoom();
      this.bindLightbox();

      window.RottenGallery = {
        showMedia: (mediaId) => this.showMedia(mediaId),
      };
    }

    showMedia(mediaId) {
      const id = String(mediaId);
      const item = this.mainItems.find((el) => el.dataset.mediaId === id);
      if (!item) return;

      this.mainItems.forEach((el) => el.classList.remove('rc-product-gallery__main-item--active'));
      item.classList.add('rc-product-gallery__main-item--active');

      this.thumbs.forEach((btn) => {
        const isMatch = btn.dataset.mediaId === id;
        btn.classList.toggle('rc-product-gallery__thumb--active', isMatch);
        btn.setAttribute('aria-selected', isMatch ? 'true' : 'false');
      });

      this.lightboxIndex = this.mainItems.indexOf(item);
    }

    bindThumbs() {
      this.thumbs.forEach((btn) => {
        btn.addEventListener('click', () => this.showMedia(btn.dataset.mediaId));
      });
    }

    bindZoom() {
      const zoomBtn = this.el.querySelector('[data-rc-gallery-zoom]');
      if (!zoomBtn) return;
      zoomBtn.addEventListener('click', () => this.openLightbox(this.lightboxIndex));
    }

    bindLightbox() {
      if (!this.lightbox) return;
      const stage = this.lightbox.querySelector('[data-rc-lightbox-stage]');
      const prevBtn = this.lightbox.querySelector('[data-rc-lightbox-prev]');
      const nextBtn = this.lightbox.querySelector('[data-rc-lightbox-next]');

      this.lightbox.querySelectorAll('[data-rc-lightbox-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeLightbox());
      });

      if (prevBtn) prevBtn.addEventListener('click', () => this.stepLightbox(-1));
      if (nextBtn) nextBtn.addEventListener('click', () => this.stepLightbox(1));

      this._lightboxStage = stage;
    }

    openLightbox(index) {
      if (!this.lightbox || !this._lightboxStage) return;
      this.lightboxIndex = index;
      this.renderLightboxSlide();
      this.lightbox.hidden = false;
      requestAnimationFrame(() => {
        this.lightbox.dataset.open = 'true';
      });
      document.body.style.overflow = 'hidden';
      this.releaseFocusTrap = trapFocus(this.lightbox, () => this.closeLightbox());
    }

    closeLightbox() {
      if (!this.lightbox) return;
      this.lightbox.dataset.open = 'false';
      document.body.style.overflow = '';
      if (this.releaseFocusTrap) this.releaseFocusTrap();
      setTimeout(() => {
        this.lightbox.hidden = true;
      }, 200);
    }

    stepLightbox(direction) {
      const total = this.mainItems.length;
      if (!total) return;
      this.lightboxIndex = (this.lightboxIndex + direction + total) % total;
      this.renderLightboxSlide();
    }

    renderLightboxSlide() {
      const item = this.mainItems[this.lightboxIndex];
      if (!item || !this._lightboxStage) return;
      const img = item.querySelector('img');
      this._lightboxStage.innerHTML = img
        ? `<img src="${img.currentSrc || img.src}" alt="${img.alt || ''}">`
        : '';
    }
  }

  document.querySelectorAll('[data-rc-gallery]').forEach((el) => new RottenGallery(el));
})();
